import { safeToString } from '@/utils/string';

/**
 * Build suggestion lists for any array of objects.
 *
 * It collects all unique values per key from the given list of items.
 * Optionally, you can limit it to specific fields.
 *
 * Example:
 *   buildFieldSuggestions([{ name: 'Jan' }, { name: 'Lisa' }, { name: 'Jan' }])
 *   → { name: ['Jan', 'Lisa'] }
 */
export function buildFieldSuggestions<T extends Record<string, any>>(
  items: T[] = [],
  options?: {
    /** restrict which fields should be considered */
    includeFields?: (keyof T)[];
    /** convert any value to string (default: String) */
    transformValue?: (val: unknown) => string;
    /** order direction: "asc" or "desc" (default: desc → newest first) */
    order?: 'asc' | 'desc';
  },
): Record<keyof T, string[]> {
  const { includeFields, transformValue, order = 'desc' } = options ?? {};
  const suggestions: Partial<Record<keyof T, string[]>> = {};

  if (!Array.isArray(items) || items.length === 0) {
    return suggestions as Record<keyof T, string[]>;
  }

  const normalize = (v: unknown): string => (transformValue ? transformValue(v) : safeToString(v));

  for (const item of items) {
    if (typeof item !== 'object' || !item) continue;

    const keys = includeFields ?? Object.keys(item);
    for (const key of keys) {
      const rawValue = item[key];
      if (rawValue == null || rawValue === '') continue;

      const val = normalize(rawValue);
      if (!val) continue;

      if (!suggestions[key]) suggestions[key] = [];
      suggestions[key]!.push(val);
    }
  }

  // Deduplicate + sort
  for (const key in suggestions) {
    const arr = suggestions[key as keyof T]!;
    const unique = Array.from(new Set(arr));
    suggestions[key as keyof T] = order === 'desc' ? unique.reverse() : unique;
  }

  return suggestions as Record<keyof T, string[]>;
}
